import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { Dispatch } from 'redux'
import {
  getMasterTrades,
  addMasterTrade,
  editMasterTrade,
  getMasterTrade,
  submitMasterTrade,
  rejectMasterTrade,
  approveMasterTrade,
  deleteMasterTrade,
  cancelMasterTrade,
  getLockedMasterTrades,
  startLoadingMasterTrades,
  stopLoadingMasterTrades,
} from 'app/state/ducks/master-trades/actions'

import {
  EditBookingDetails,
  EditCounterpartiesDetails,
  EditReferencesDetails,
  EditTermsOfTradesDetails,
  MasterTradeParams,
  EditAssets,
} from 'app/models/master-trade-params'
import { StableKey } from 'app/models/common-types'
import { AAMetadataResponse } from 'app/models/aa-metadata-params'
import {
  coalesceThunk,
  coalesceThunkByKey,
} from 'app/state/utils/coalesce-thunk'

export const saveMasterTrade =
  (savedMasterTrade: MasterTradeParams, masterTrade?: any) =>
  async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl

    if (masterTrade) {
      console.log('We don\'t support editing drafts')
      return { success: false, payload: 'Editing draft trades is not supported' }
    } else {
      return client
        .post(`${apiServerUrl}/api/draft-master-trades`, savedMasterTrade)
        .then(masterTrade => {
          dispatch(addMasterTrade(masterTrade.data))
          return { success: true, payload: masterTrade.data }
        })
        .catch(e => {
          return { success: false, payload: e.response.data.error }
        })
    }
  }

export const editBookingDetails =
  (bookingDetails: EditBookingDetails, masterTrade?: any) =>
  async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl

    return client
      .post(`${apiServerUrl}/api/approved-master-trades/booking`, {
        ...bookingDetails,
        masterTradeId: { id: masterTrade.id, admin: masterTrade?.agencyAdmin },
      })
      .then(masterTrade => {
        dispatch(editMasterTrade(masterTrade.data))
        return { success: true, payload: masterTrade.data }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const editReferencesDetails =
  (referencesDetails: EditReferencesDetails, masterTrade?: any) =>
  async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl

    return client
      .post(`${apiServerUrl}/api/approved-master-trades/references`, {
        ...referencesDetails,
        masterTradeId: { id: masterTrade.id, admin: masterTrade?.agencyAdmin },
      })
      .then(masterTrade => {
        dispatch(editMasterTrade(masterTrade.data))
        return { success: true, payload: masterTrade.data }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const editCounterpartiesDetails =
  (counterpartiesDetails: EditCounterpartiesDetails, masterTrade?: any) =>
  async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl

    return client
      .post(`${apiServerUrl}/api/approved-master-trades/counterparties`, {
        ...counterpartiesDetails,
        masterTradeId: { id: masterTrade.id, admin: masterTrade?.agencyAdmin },
      })
      .then(masterTrade => {
        dispatch(editMasterTrade(masterTrade.data))
        return { success: true, payload: masterTrade.data }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const editTermsOfTradeDetails =
  (termsOfTradeDetails: EditTermsOfTradesDetails, masterTrade?: any) =>
  async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl

    return client
      .post(`${apiServerUrl}/api/approved-master-trades/terms-of-trades`, {
        ...termsOfTradeDetails,
        masterTradeId: { id: masterTrade.id, admin: masterTrade?.agencyAdmin },
      })
      .then(masterTrade => {
        dispatch(editMasterTrade(masterTrade.data))
        return { success: true, payload: masterTrade.data }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const editTradeAssets =
  (assets: EditAssets, masterTrade?: any) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl

    return client
      .post(`${apiServerUrl}/api/approved-master-trades/assets`, {
        ...assets,
        masterTradeId: { id: masterTrade.id, admin: masterTrade?.agencyAdmin },
      })
      .then(masterTrade => {
        dispatch(editMasterTrade(masterTrade.data))
        return { success: true, payload: masterTrade.data }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

const loadApprovedMasterTrades = async () => {
  const apiServerUrl = config.backendUrl
  return await client
    .get(`${apiServerUrl}/api/approved-master-trades`)
    .then(response => {
      return response.data
    })
    .catch(e => {
      console.log(e)
      return []
    })
}

const loadDraftMasterTrades = async () => {
  const apiServerUrl = config.backendUrl
  return await client
    .get(`${apiServerUrl}/api/draft-master-trades`)
    .then(response => {
      return response.data
    })
    .catch(e => {
      console.log(e)
      return []
    })
}

const loadSubmittedMasterTrades = async () => {
  const apiServerUrl = config.backendUrl
  return await client
    .get(`${apiServerUrl}/api/submitted-master-trades`)
    .then(response => {
      return response.data
    })
    .catch(e => {
      console.log(e)
      return []
    })
}

const loadSettledMasterTrades = async () => {
  const apiServerUrl = config.backendUrl
  return await client
    .get(`${apiServerUrl}/api/locked-master-trades`)
    .then(response => {
      return response.data
    })
    .catch(e => {
      console.log(e)
      return []
    })
}

export const loadAllMasterTrades = coalesceThunk(
  () => async (dispatch: Dispatch) => {
    try {
      dispatch(startLoadingMasterTrades())
      const [
        draftMasterTrades,
        submittedMasterTrades,
        approvedMasterTrades,
        lockedMasterTrades,
      ] = await Promise.all([
        loadDraftMasterTrades(),
        loadSubmittedMasterTrades(),
        loadApprovedMasterTrades(),
        loadSettledMasterTrades(),
      ])

      const subMasterTrades = submittedMasterTrades.map(
        (a: { masterTrade: any }) => ({
          ...a.masterTrade,
          status: 'Submitted',
        }),
      )

      const settledMasterTrades = lockedMasterTrades.map(
        (a: { masterTrade: any; status: string }) => ({
          ...a.masterTrade,
          status: a.status ?? '',
        }),
      )

      const allMasterTrades = draftMasterTrades
        .map((masterTrade: MasterTradeParams) => ({
          ...masterTrade,
          status: 'Draft',
        }))
        .concat(subMasterTrades)
        .concat(
          approvedMasterTrades.map((masterTrade: MasterTradeParams) => ({
            ...masterTrade,
            status: 'Open',
          })),
        )
        .concat(settledMasterTrades)
      return dispatch(getMasterTrades(allMasterTrades))
    } catch (error) {
      console.log('THUNK ERROR: ', error)
    } finally {
      dispatch(stopLoadingMasterTrades())
    }
  },
)

export const loadOneMasterTrade = coalesceThunkByKey(
  (masterTradeId: string, status: string) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    const statusLower =
      status.toLowerCase() == 'terminated' ? 'locked' : status.toLowerCase()
    return client
      .get(
        `${apiServerUrl}/api/${statusLower}-master-trades/one?id=${masterTradeId}`,
      )
      .then(masterTrades => {
        if (statusLower === 'submitted' || statusLower === 'locked') {
          return dispatch(
            getMasterTrade({
              ...masterTrades.data.masterTrade,
              status: status,
            }),
          )
        }
        return dispatch(
          getMasterTrade({ ...masterTrades.data, status: status }),
        )
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  },
)

export const setOneMasterTrade =
  (masterTrade: any) => async (dispatch: Dispatch) => {
    dispatch(getMasterTrade({ ...masterTrade }))
  }

export const submitForApproval =
  (masterTrade: MasterTradeParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/draft-master-trades/submit`, {
        id: masterTrade.id,
      })
      .then(response => {
        dispatch(submitMasterTrade(response.data))
        return { success: true, payload: masterTrade }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const rejectSubmittedMasterTrade =
  (masterTrade: MasterTradeParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/submitted-master-trades/reject`, {
        id: masterTrade.id,
      })
      .then(response => {
        dispatch(rejectMasterTrade(response.data))
        return { success: true, payload: response.data }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const approveSubmittedMasterTrade =
  (masterTrade: MasterTradeParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/submitted-master-trades/approve`, {
        id: masterTrade.id,
      })
      .then(response => {
        dispatch(approveMasterTrade(response.data))
        return { success: true, payload: masterTrade }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const deleteDraftMasterTrade =
  (id: string) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .delete(`${apiServerUrl}/api/draft-master-trades?ids[]=${id}`)
      .then(response => {
        dispatch(deleteMasterTrade(response.data))
        return response.data && response.data?.length > 0
          ? {
              payload: response.data[0].id,
              success: response.data[0].isSuccess,
            }
          : {
              payload:
                'Unable to delete draft trade.\n The operation could not be completed due to an unexpected issue.',
              success: false,
            }
      })
      .catch(e => {
        console.log('THUNK ERROR', e)
        return {
          payload: e.response.data.error,
          success: false,
        }
      })
  }

export const loadLockedMasterTrades = coalesceThunk(
  () => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .get(`${apiServerUrl}/api/locked-master-trades`)
      .then(response => {
        const lockedMasterTrades = response.data.map(
          (a: {
            masterTrade: any
            locked: string
            status?: string
            reason?: string
          }) => ({
            ...a.masterTrade,
            status: a.status,
            reason: a.reason,
            terminationDate: a.locked,
          }),
        )

        return dispatch(getLockedMasterTrades(lockedMasterTrades))
      })
      .catch(error => {
        console.log('THUNK ERROR: ', error)
      })
  },
)

export const cancelApprovedMasterTrade =
  (masterTradeId: StableKey, reason: string) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/approved-master-trades/cancel`, {
        masterTradeId,
        reason,
      })
      .then(response => {
        dispatch(cancelMasterTrade(response.data))
        return { success: true, payload: response.data }
      })
      .catch(e => {
        console.log('THUNK ERROR', e)
        return {
          success: false,
          payload: e.response?.data?.error || 'Failed to cancel master trade',
        }
      })
  }

export const fetchFullySignedAAMetadataByDateRange = async (
  from: string | undefined,
  to: string | undefined,
  includeUnsigned = false,
): Promise<AAMetadataResponse> => {
  const apiServerUrl = config.backendUrl
  try {
    const response = await client.get(
      `${apiServerUrl}/api/approved-master-trades/aa-metadata/by-date-range`,
      { params: { from, to, includeUnsigned: includeUnsigned || undefined } },
    )
    return { success: true, data: response.data }
  } catch (e: any) {
    console.log('THUNK ERROR: Failed to fetch fully signed AA metadata by date range', e)
    return {
      success: false,
      data: [],
      error:
        e.response?.data?.error ||
        'Failed to fetch fully signed AA metadata by date range',
    }
  }
}
